// CollectionsNav — sidebar nav for the Collections (Operations) module.
//
// Group structure (top → bottom):
//   1. Top (no label)  — Dashboard, Diary, Bookings, Pendings,
//                        Customers, Cancellations, GeoLink.
//   2. FINANCE         — Receipts, Invoices, Payment Run.
//   3. ADMIN           — Reports, Integrations, Admin.
//   4. (no label)      — Settings.
//
// "Book In", "New Receipt", and "Lookup" are NOT in this nav — they
// live exclusively on the NarrowSidebar floating quick-access cubes on
// the right edge of the viewport. Settings sits at the bottom of this
// nav so all destinations a user might want from the sidebar are in
// one place.
//
// Decisions baked in:
//   - Active state via React Router NavLink — primary-tint background +
//     left accent.
//   - Collapse state comes from SidebarContext: outer SidebarShell
//     applies the width transition; CollectionsNav uses the same flag
//     to hide labels and section headings while the rail is collapsed.

import { type ComponentType } from 'react';
import { NavLink } from 'react-router-dom';
import {
  Coins,
  FileText,
  Hourglass,
  LayoutDashboard,
  ListChecks,
  MapPin,
  Plug,
  Receipt,
  Settings,
  ShieldCheck,
  Truck,
  Users,
  XOctagon,
} from 'lucide-react';
import type { LucideProps } from 'lucide-react';
import { cn } from '../../../utils/cn';
import { useAuthStore } from '../../../store/authStore';
import { useSidebar } from '../../../components/shell/SidebarContext';
import type { UserSummary } from '../../../types/api';
import { useCollectionsPendingCount } from '../hooks/useCollectionsPendingCount';
import styles from './CollectionsNav.module.css';

type Role = UserSummary['role'];

interface NavItem {
  to: string;
  label: string;
  icon: ComponentType<LucideProps>;
  /** When omitted, item is visible to any authenticated staff. */
  roles?: Role[];
  /** When true, an exact match is required for active state. */
  end?: boolean;
  badgeKey?: 'pending_count';
}

interface NavSection {
  label?: string;
  /** When provided, the whole section is hidden unless the user role matches. */
  roles?: Role[];
  items: NavItem[];
}

const STAFF_ROLES: Role[] = ['staff', 'admin'];
const ADMIN_ROLES: Role[] = ['admin'];

const SECTIONS: NavSection[] = [
  {
    // Primary workflow nav. Lookup lives on the NarrowSidebar — no
    // duplicate here.
    items: [
      { to: '/collections/dashboard', label: 'Dashboard', icon: LayoutDashboard, end: true, roles: STAFF_ROLES },
      { to: '/collections/diary', label: 'Diary', icon: Truck, roles: STAFF_ROLES },
      { to: '/collections/bookings', label: 'Bookings', icon: ListChecks, end: true, roles: STAFF_ROLES },
      { to: '/collections/pending', label: 'Pendings', icon: Hourglass, roles: STAFF_ROLES, badgeKey: 'pending_count' as const },
      { to: '/customers', label: 'Customers', icon: Users, roles: STAFF_ROLES },
      { to: '/collections/cancellations', label: 'Cancellations', icon: XOctagon, roles: STAFF_ROLES },
      { to: '/collections/geolink', label: 'GeoLink', icon: MapPin, roles: STAFF_ROLES },
    ],
  },
  {
    label: 'Finance',
    items: [
      { to: '/collections/receipts', label: 'Receipts', icon: Receipt, roles: STAFF_ROLES },
      { to: '/collections/invoices', label: 'Invoices', icon: FileText, roles: ADMIN_ROLES },
      { to: '/collections/payment-run', label: 'Payment Run', icon: Coins, roles: ADMIN_ROLES },
    ],
  },
  {
    label: 'Admin',
    roles: ADMIN_ROLES,
    items: [
      {
        // Integrations landing — staff-readable; QB sub-routes are
        // admin-gated inside the page cards.
        to: '/collections/integrations',
        label: 'Integrations',
        icon: Plug,
        roles: ADMIN_ROLES,
      },
      { to: '/admin', label: 'Admin', icon: ShieldCheck, roles: ADMIN_ROLES },
    ],
  },
  {
    // Standalone tail group — Settings is visible to any staff/admin user
    // (drivers don't reach this UI). Lives here rather than on the
    // quick-access cubes so the three cubes can stay reserved for
    // high-frequency actions.
    items: [
      { to: '/collections/settings', label: 'Collections', icon: Settings, roles: STAFF_ROLES },
    ],
  },
];

export function CollectionsNav(): JSX.Element | null {
  const role = useAuthStore((s) => s.user?.role);
  const { isCollapsed } = useSidebar();
  const pendingCountQuery = useCollectionsPendingCount();
  const pendingCount = pendingCountQuery.data ?? 0;

  function visible(roles?: Role[]): boolean {
    if (!roles) return true;
    if (!role) return false;
    return roles.includes(role);
  }

  function badgeFor(key?: NavItem['badgeKey']): number | null {
    if (key === 'pending_count') return pendingCount > 0 ? pendingCount : null;
    return null;
  }

  return (
    <div className={cn(styles.nav, isCollapsed && styles.collapsed)}>
      {SECTIONS.map((section, idx) => {
        if (!visible(section.roles)) return null;
        const items = section.items.filter((it) => visible(it.roles));
        if (items.length === 0) return null;
        return (
          <div key={idx} className={styles.section}>
            {section.label ? (
              <span className={styles.sectionLabel}>{section.label}</span>
            ) : null}
            <ul className={styles.list}>
              {items.map((item) => {
                const badge = badgeFor(item.badgeKey);
                return (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end={item.end}
                      className={({ isActive }) => cn(styles.link, isActive && styles.linkActive)}
                      title={isCollapsed ? item.label : undefined}
                    >
                      <span className={styles.linkInner}>
                        <span className={styles.icon}>
                          <item.icon size={18} aria-hidden />
                        </span>
                        <span className={styles.label}>{item.label}</span>
                        {badge !== null ? (
                          <span className={styles.badge} aria-label={`${badge} pending`}>
                            {badge}
                          </span>
                        ) : null}
                      </span>
                    </NavLink>
                  </li>
                );
              })}
            </ul>
          </div>
        );
      })}
    </div>
  );
}
